import type { TContentContext } from '~/features-shared/config-manager/interfaces/contexts'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { IDerivedTypeData } from '~/features-shared/content-derived-type/interfaces/derived-types'
import { EDerivedTypeParent } from '~/features-shared/content-derived-type/interfaces/derived-types'
import type { Acl, DerivedTypeAcl } from '~/features-shared/libs/interfaces/authorization'

const v2DerivedTypePrefix = {
  ideation: 'dt-idea', // Legacy
  idea: 'dt-idea',
  post: 'dt-post',
}

const parentDTMap = new Map([
  [EDerivedTypeParent.IDEA, v2DerivedTypePrefix.idea],
  [EDerivedTypeParent.POST, v2DerivedTypePrefix.post],
])

export function isDerivedTypeModel(content: TContentContext) {
  return content === IContentModel.DerivedIdea || content === IContentModel.DerivedPost
}

export function derivedTypeModelV2(content: IContent) {
  if (!content.derived_type)
    return null

  const parent = content.derived_type.parent
  const prefix = parentDTMap.get(parent)

  return `${prefix}-${content.derived_type.id}` as IContentModel
}

export function getDerivedTypeIdFromV2Model(modelString: string) {
  if (modelString.startsWith(v2DerivedTypePrefix.idea))
    return modelString.replace(`${v2DerivedTypePrefix.idea}-`, '')

  if (modelString.startsWith(v2DerivedTypePrefix.post))
    return modelString.replace(`${v2DerivedTypePrefix.post}-`, '')
}

/**
 * Return complete derived type id with parent type and id
 * @param {string} type - Parent type
 * @param {string} id - Derived type ID
 * @returns {string | undefined}
 * @example
 * ('post', 'IpVXZBLPQHGn7u_gi6HpoQ') -> 'dt-post-IpVXZBLPQHGn7u_gi6HpoQ'
 */
export function getDerivedTypeIdFromTypeAndId(type: string, id: string): string | undefined {
  const keyType = (Object.keys(v2DerivedTypePrefix) as Array<keyof typeof v2DerivedTypePrefix>)
    .find(key => key === type)

  if (keyType)
    return `${v2DerivedTypePrefix[keyType]}-${id}`

  return undefined
}

export function getDerivedTypeParentModel(content: TContentContext) {
  if (content === IContentModel.DerivedIdea)
    return IContentModel.Idea

  if (content === IContentModel.DerivedPost)
    return IContentModel.Post
}

export function getDerivedTypeFromV2Model(modelString: string) {
  if (modelString.startsWith(v2DerivedTypePrefix.idea))
    return IContentModel.DerivedIdea

  if (modelString.startsWith(v2DerivedTypePrefix.post))
    return IContentModel.DerivedPost
}

export function createStyles(derivedType: IDerivedTypeData) {
  if (!derivedType.id || !derivedType.color)
    return
  const css = `
    .bg-dt-${derivedType.id} {
      background-color: ${derivedType.color};
    }
    .text-dt-${derivedType.id} {
      color: ${derivedType.color};
    }
    .border-dt-${derivedType.id} {
      border-color: ${derivedType.color};
    }
    .dropdown-menu .dropdown-menu-wrap .item .text-dt-${derivedType.id} {
      color: ${derivedType.color};
    }
  `
  const { load } = useStyleTag(css, { id: derivedType.id, immediate: true, manual: true })
  load() // Automatic load doesn't work when we reload page
}

export function derivedTypeACLParser(dtAcl: DerivedTypeAcl) {
  const acl = { ...dtAcl }
  const _acl = <Acl>{}

  Object.keys(acl)
    .forEach((key) => {
      if (key === 'create') {
        _acl.update = {
          access: acl.create!,
        }
      }
    })

  return _acl
}

interface TagActionsTag {
  object: {
    literal: string
  }
  predicate: string
}
interface TagAction {
  subject: string
  tag: TagActionsTag[]
}
export interface TagActions {
  add: TagAction[]
  remove: TagAction[]
}

export function tagsForV2APIs(tagActions?: TagActions) {
  if (!tagActions)
    return

  const result: Record<string, object[]> = {}
  if (tagActions.add && tagActions.add.length) {
    const tags = tagActions.add[0].tag
    result.add = tags.map(tag => ({
      tag: tag.object.literal,
      predicate: tag.predicate,
    }))
  }
  if (tagActions.remove && tagActions.remove.length) {
    const tags = tagActions.remove[0].tag
    result.remove = tags.map(tag => ({
      tag: tag.object.literal,
      predicate: tag.predicate,
    }))
  }
  return result
}

/**
 * Extract content type from derived type string
 *
 * @returns dtData - Object containing derived type data
 * @example
 * dt-idea-12345xyz -> {
 *  contentType: 'idea',
 *  derivedTypeId: '12345xyz'
 * }
 */
export function extractContentType(type: string): {
  /** Parent content type */
  contentType?: string
  /** Derived type Id */
  derivedTypeId?: string
} {
  const dtParent = getDerivedTypeFromV2Model(type) // e.g. dt-idea-1234 => derivedidea
  if (dtParent) {
    const parent = getDerivedTypeParentModel(dtParent) // e.g derivedidea => idea
    return {
      contentType: parent,
      derivedTypeId: getDerivedTypeIdFromV2Model(type),
    }
  }
  return {
    contentType: type,
  }
}
